/**
 * The answer panel: either the top-ranked places to be right now, or the detail of a
 * cell the rider tapped.
 */

import type { ScoredCell } from '../scoring/score.js';
import type { CellDescription } from '../scoring/describe.js';

interface TopListProps {
  cells: readonly ScoredCell[];
  describe: (h3: string) => CellDescription;
  selected: string | null;
  onSelect: (h3: string) => void;
}

/** Rank badge, so the ordering survives a glance without reading the numbers. */
function Rank({ index }: { index: number }): JSX.Element {
  return <span className="rank-badge">{index + 1}</span>;
}

/** Ride time in minutes. Anything under a minute is "here", not "0 min". */
function rideTime(seconds: number): string {
  return seconds < 60 ? 'here' : `${Math.round(seconds / 60)} min`;
}

export function TopCells({ cells, describe, selected, onSelect }: TopListProps): JSX.Element {
  if (cells.length === 0) {
    return <p className="empty-note">Nothing open and reachable at this hour.</p>;
  }

  return (
    <ol className="top-cells">
      {cells.map((cell, index) => {
        const description = describe(cell.h3);
        const landmarks = description.landmarks.slice(0, 2).map((l) => l.name);

        return (
          <li key={cell.h3}>
            <button
              type="button"
              className={selected === cell.h3 ? 'cell-row cell-row-selected' : 'cell-row'}
              onClick={() => onSelect(cell.h3)}
            >
              <Rank index={index} />
              <span className="cell-row-body">
                <span className="cell-row-landmarks">
                  {landmarks.length > 0 ? landmarks.join(' · ') : 'Unnamed block'}
                </span>
                <span className="cell-row-meta">
                  {description.openVenues} open within 6 min
                </span>
              </span>
              <span
                className="cell-row-score"
                title="Relative to the best cell at this moment"
              >
                {Math.round(cell.normalized * 100)}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

interface DetailProps {
  cell: ScoredCell;
  description: CellDescription;
  onClear: () => void;
}

export function CellDetail({ cell, description, onClear }: DetailProps): JSX.Element {
  return (
    <div className="cell-detail">
      <div className="cell-detail-head">
        <div>
          <div className="cell-detail-score">{Math.round(cell.normalized * 100)}</div>
          {/* Against the week's peak, not this moment's — so 30 genuinely means
              "a third as good as the best hour of the week", not "third best right now". */}
          <div className="cell-detail-score-label">vs. the week&rsquo;s best hour</div>
        </div>
        <button type="button" className="clear-button" onClick={onClear}>
          Back to top spots
        </button>
      </div>

      <dl className="cell-stats">
        <div>
          <dt>Open within 6 min</dt>
          <dd>{description.openVenues}</dd>
        </div>
        <div>
          <dt>Hours guessed</dt>
          <dd>{Math.round(description.guessedShare * 100)}%</dd>
        </div>
      </dl>

      {description.guessedShare > 0.5 && (
        <p className="caveat">
          Most venues here have no published hours, so this cell leans on category
          averages. Treat it as a weaker signal than a cell with known hours.
        </p>
      )}

      {description.landmarks.length > 0 && (
        <>
          <h3 className="section-label">Nearest open</h3>
          <ul className="landmark-list">
            {description.landmarks.map((landmark) => (
              <li key={landmark.name + landmark.seconds}>
                <span className="landmark-name">{landmark.name}</span>
                <span className="landmark-meta">
                  {landmark.category.replace(/_/g, ' ')} · {rideTime(landmark.seconds)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
